import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RotateCw, Volume2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import StoreScopeTag from "@/components/partner/StoreScopeTag";
import PaymentAnnouncer from "@/components/engage/PaymentAnnouncer";

/**
 * Iter-616 — "🔊 Counter sound box" (Xiamen Magnetic North cloud speaker).
 * The speaker announces every paid till amount; the backend broadcast is
 * config-gated server-side. This card only stores the device number:
 *   PUT /vendors/me/sound-devices {devices:[{device_number, active}]}
 *   (empty array removes it). Device setup itself lives in the
 *   supplier's DM Tools dashboard. KYC-approved vendor owners only;
 *   X-Store-Id is injected by the sharedApi interceptor.
 */
const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

export default function SoundBoxCard() {
  const qc = useQueryClient();
  const [device, setDevice] = useState("");
  const [seeded, setSeeded] = useState(false);

  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me", OPTS)).data,
  });

  const saved = meQ.data?.sound_devices?.[0]?.device_number || "";

  // Iter-627 — last 10 announcements (sent / failed).
  const logQ = useQuery({
    queryKey: ["soundbox-log"],
    enabled: !!saved,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/sound-devices/log?limit=10", OPTS)).data,
  });

  useEffect(() => {
    if (!seeded && meQ.data) {
      setDevice(saved);
      setSeeded(true);
    }
  }, [meQ.data, saved, seeded]);

  // Iter 660 — store-level announcement language (EN / हिन्दी).
  const langMut = useMutation({
    mutationFn: async (announce_lang) =>
      (await sharedApi.put("/classifieds/engagement/vendors/me/announce-lang", { announce_lang }, OPTS)).data,
    onSuccess: () => {
      toast.success("Announcement language updated.");
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't change the language.");
    },
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const v = device.trim();
      const devices = v ? [{ device_number: v, active: true }] : [];
      return (await sharedApi.put("/classifieds/engagement/vendors/me/sound-devices", { devices }, OPTS)).data;
    },
    onSuccess: (_d, _v) => {
      toast.success(
        device.trim()
          ? "✅ Saved — the speaker will announce every paid till amount once the sound box service is activated."
          : "Sound box removed.",
      );
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save the device — please try again.");
    },
  });

  // KYC-approved vendor owners only.
  if (!meQ.data || meQ.data.kyc_status !== "approved") return null;

  return (
    <section data-testid="soundbox-card" className="mt-6 rounded-2xl bg-white shadow-card p-6 md:p-8">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-9 h-9 rounded-xl bg-indigo-100 text-indigo-600 grid place-items-center shrink-0">
          <Volume2 className="w-4 h-4" />
        </span>
        <h2 className="font-display text-2xl font-black text-slate-900">🔊 Counter sound box</h2>
        <StoreScopeTag />
      </div>
      <p className="mt-1 text-sm text-slate-500">
        The cloud speaker announces every paid till amount at your counter. Enter the device number
        printed on your sound box — device setup itself is managed in the supplier&apos;s DM Tools dashboard.
      </p>
      {/* Iter 660 — software announcer: this device speaks each paid bill.
          Language is store-level (EN/हिन्दी chips); on/off is per device. */}
      <div className="mt-4">
        <PaymentAnnouncer
          prefix="owner-announce"
          storageKey="announce:owner"
          fetchFeed={async () =>
            (await sharedApi.get("/classifieds/engagement/vendors/me/paid-feed", OPTS)).data
          }
          requestTts={async (session_id, lang) =>
            (await sharedApi.post("/classifieds/engagement/announce/tts", { session_id, lang })).data
          }
          langControl={
            <div className="mt-2 flex items-center gap-1.5">
              {[
                { key: "en", label: "English" },
                { key: "hi", label: "हिन्दी" },
              ].map((l) => {
                const current = meQ.data?.announce_lang || "en";
                const on = current === l.key;
                return (
                  <button
                    key={l.key}
                    type="button"
                    data-testid={`announce-lang-${l.key}`}
                    disabled={langMut.isPending}
                    onClick={() => !on && langMut.mutate(l.key)}
                    className={`rounded-full px-3 py-1 text-[11px] font-bold border transition-colors ${
                      on
                        ? "bg-[color:var(--brand-orange)] text-white border-transparent"
                        : "bg-white text-slate-600 border-amber-300 hover:border-[color:var(--brand-orange)]"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          }
        />
      </div>
      <form
        className="mt-4 flex items-center gap-2 flex-wrap"
        onSubmit={(e) => {
          e.preventDefault();
          saveMut.mutate();
        }}
      >
        <input
          value={device}
          onChange={(e) => setDevice(e.target.value)}
          placeholder="Device number, e.g. MN00123456"
          maxLength={40}
          data-testid="soundbox-device-input"
          className="flex-1 min-w-[220px] rounded-full border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:border-slate-500"
        />
        <button
          type="submit"
          disabled={saveMut.isPending || device.trim() === saved}
          data-testid="soundbox-save-btn"
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-6 py-2.5 disabled:opacity-50 transition-colors"
        >
          {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved && !device.trim() ? "Remove" : "Save"}
        </button>
      </form>
      {saved && (
        <p className="mt-2 text-xs text-slate-500" data-testid="soundbox-current">
          Current device: <span className="font-semibold text-slate-700">{saved}</span> — clear the field and save to remove it.
        </p>
      )}
      {/* Iter-627 — announcement log: ✅ played / ❌ reason per till sale. */}
      {saved && logQ.data && (
        <div className="mt-4" data-testid="sbx-log">
          <div className="flex items-center gap-2">
            <span className="text-xs font-extrabold text-slate-900">Last announcements</span>
            <button
              type="button"
              data-testid="sbx-log-refresh"
              onClick={() => logQ.refetch()}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-[color:var(--brand-blue)] hover:underline"
            >
              <RotateCw className={`w-3 h-3 ${logQ.isFetching ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          {(logQ.data.items || []).length === 0 ? (
            <p className="mt-1 text-xs text-slate-500">None yet — they appear here after each paid till sale.</p>
          ) : (
            <ul className="mt-1">
              {(logQ.data.items || []).map((r, i) => (
                <li
                  key={i}
                  data-testid={`sbx-log-row-${i}`}
                  className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0 text-xs"
                >
                  <span className="text-slate-600">
                    {r.ok ? "✅" : "❌"} ${Number(r.amount).toFixed(2)}
                    {r.is_test ? " (test)" : ""} ·{" "}
                    {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{" "}
                    {new Date(r.created_at).toLocaleDateString()}
                  </span>
                  <span className={`font-bold ${r.ok ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.ok ? "played" : String(r.status || "failed").replace(/^api_|^error:/, "")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
